import React from "react";
import { useHistory } from "react-router-dom";
import { NamedProperty, BreadcrumbItem, Breadcrumb } from "sprint-common-web";

import { ContentSubtype } from "../../../../../common/types/ContentSubtype";
import { MovieContentSubtype } from "../../../../../common/types/MovieContentSubtype";
import { VersionSummary } from "../../../../../common/types/VersionSummary";
import { assetsSearchRoutes, getAssetsSearchPageHref, getMovieAssetsListHref } from "../../../../AssetsRoutes";

interface MovieDetailsPageBreadcrumbsProps {
    contentType: MovieContentSubtype;
    movie: NamedProperty;
    version: VersionSummary;
}

export const MovieDetailsPageBreadcrumbs: React.FunctionComponent<MovieDetailsPageBreadcrumbsProps> = (
    props: MovieDetailsPageBreadcrumbsProps
) => {
    const history = useHistory();

    const { contentType, movie, version } = props;

    const goToAssetsSearchPage = () => {
        const href = getAssetsSearchPageHref(contentType);
        history.push(href);
    };

    const goToMovieAssetsList = (movieId: string, versionSummary?: VersionSummary) => {
        const versionTypeIds = versionSummary ? [versionSummary.versionType.id] : undefined;
        const distributorIds = versionSummary?.distributors.map((distributor: NamedProperty) => distributor.id);
        const brandingIds = versionSummary?.brandings?.map((branding: NamedProperty) => branding.id);

        const href = getMovieAssetsListHref(movieId, { versionTypeIds, distributorIds, brandingIds });
        history.push(href);
    };

    const items: BreadcrumbItem[] = [
        ...assetsSearchRoutes.baseRouteNames.map((name: string) => {
            return { key: name, text: name };
        }),
        {
            key: "search-movies-breadcrumb",
            text: resolveContentTypeName(contentType),
            onClick: goToAssetsSearchPage
        },
        {
            key: "movie-assets-list-breadcrumb",
            text: movie.name,
            onClick: () => goToMovieAssetsList(movie.id)
        },
        {
            key: "movie-version-assets-list-breadcrumb",
            text: getMovieVersionText(version),
            onClick: () => goToMovieAssetsList(movie.id, version)
        }
    ];

    return <Breadcrumb items={items} />;
};

const getMovieVersionText = (version: VersionSummary): string => {
    const distributorNames = version.distributors.map((distributor: NamedProperty) => distributor.name).join(", ");
    const brandingNames = version.brandings?.map((distributor: NamedProperty) => distributor.name).join(", ");

    const movieVersionTextParts = [distributorNames, version.versionType.name, brandingNames].filter(
        (part: string | undefined) => !!part
    );

    return movieVersionTextParts.join(" - ");
};

const resolveContentTypeName = (contentType: MovieContentSubtype) => {
    switch (contentType) {
        case ContentSubtype.Movie:
            return "Movies";

        case ContentSubtype.TvSingle:
            return "TV Singles";

        case ContentSubtype.FeatureDocumentary:
            return "Feature Documentaries";

        case ContentSubtype.ShortFilm:
            return "Short Films";

        default:
            throw new Error(`'${contentType}' is not movie content type.`);
    }
};
